import { Button, Card, notification, Skeleton, Tooltip, Typography } from 'antd'
import { useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUsers,
  faPenToSquare,
  faClipboardCheck,
  faCircleCheck,
  faFileInvoiceDollar,
  faEnvelope,
  faDollarSign,
  faArrowRight,
  faArrowsRotate,
} from '@fortawesome/pro-solid-svg-icons'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import dayjs from 'dayjs'

import { centsToDollarsCompact, percentageDisplay } from '../../displayFormatters'
import { ComputedStage, computeStages } from './funnelModel'
import { FunnelHistorySnapshot } from './historyModel'
import { useInvoicingFunnelHistory, useInvoicingFunnelHistoryRefresh } from './historyQueries'
import './InvoicingFunnel.css'
import './InvoicingFunnelHistory.css'

const { Title, Text } = Typography

type StageConfig = {
  icon: IconDefinition
  color: string
}

const STAGE_CONFIG: Record<string, StageConfig> = {
  'Total Tenants':                 { icon: faUsers,              color: '#1890ff' },
  'Started Merchant Application':  { icon: faPenToSquare,        color: '#722ed1' },
  'Finished Merchant Application': { icon: faClipboardCheck,     color: '#fa8c16' },
  'Application Approved':          { icon: faCircleCheck,        color: '#52c41a' },
  'Created First Invoice':         { icon: faFileInvoiceDollar,  color: '#13c2c2' },
  'Sent First Invoice':            { icon: faEnvelope,           color: '#2f54eb' },
  'First Payment Received':        { icon: faDollarSign,         color: '#eb2f96' },
}

type DisplayStage = ComputedStage & {
  icon: IconDefinition
  color: string
}

type FunnelStageProps = {
  stage: DisplayStage
  showOverall: boolean
}

const FunnelStage = ({ stage, showOverall }: FunnelStageProps) => (
  <div className="funnel-stage">
    <div className="funnel-icon" style={{ backgroundColor: stage.color }}>
      <FontAwesomeIcon icon={stage.icon} />
    </div>
    <span className="funnel-count">{stage.count.toLocaleString()}</span>
    <span className="funnel-volume">
      {centsToDollarsCompact(stage.invoiceVolume)}
      {stage.invoiceVolume > 0 && (
        <span className="funnel-volume-take">
          ({centsToDollarsCompact(stage.invoiceVolume * 0.008)})
        </span>
      )}
    </span>
    <span className="funnel-label">{stage.stage}</span>
    {stage.stepConversion !== null && (
      <span className="funnel-conversion">
        <span className="funnel-conversion-step" style={{ color: stage.color }}>
          {percentageDisplay(stage.stepConversion)}
        </span>
        {showOverall && stage.overallConversion !== null && (
          <span className="funnel-conversion-overall">
            ({percentageDisplay(stage.overallConversion)})
          </span>
        )}
      </span>
    )}
  </div>
)

type FunnelArrowProps = {
  dropoff: number
}

const FunnelArrow = ({ dropoff }: FunnelArrowProps) => (
  <div className="funnel-arrow">
    <FontAwesomeIcon icon={faArrowRight} className="funnel-arrow-icon" />
    <span className="funnel-arrow-dropoff">-{dropoff.toLocaleString()}</span>
  </div>
)

const buildDisplayStages = (snapshot: FunnelHistorySnapshot): DisplayStage[] =>
  computeStages(snapshot.stages).map((s) => {
    const config = STAGE_CONFIG[s.stage] ?? { icon: faUsers, color: '#8c8c8c' }
    return { ...s, icon: config.icon, color: config.color }
  })

type SnapshotRowProps = {
  snapshot: FunnelHistorySnapshot
  isLatest: boolean
}

const SnapshotRow = ({ snapshot, isLatest }: SnapshotRowProps) => {
  const stages = useMemo(() => buildDisplayStages(snapshot), [snapshot])
  return (
    <div className="history-snapshot">
      <div className="history-snapshot-header">
        <Text strong className="history-snapshot-date">
          {dayjs(snapshot.snapshotDate).format('ddd, MMM D, YYYY')}
        </Text>
        {isLatest && <Text type="secondary" className="history-snapshot-tag">latest</Text>}
      </div>
      <div className="funnel-row">
        {stages.map((stage, i) => (
          <div key={stage.stage} style={{ display: 'flex', alignItems: 'flex-start' }}>
            {i > 0 && stage.dropoff !== null && <FunnelArrow dropoff={stage.dropoff} />}
            <FunnelStage stage={stage} showOverall={i >= 2} />
          </div>
        ))}
      </div>
    </div>
  )
}

const InvoicingFunnelHistory = () => {
  const { data, isLoading } = useInvoicingFunnelHistory()
  const refreshMutation = useInvoicingFunnelHistoryRefresh()

  if (isLoading || !data) {
    return <Skeleton active />
  }

  return (
    <Card className="funnel-card">
      <div className="funnel-header">
        <div className="funnel-header-left">
          <Title level={3}>Invoicing Adoption Funnel — Weekly History</Title>
        </div>
        <div className="funnel-header-controls">
          <Tooltip title="Refresh backend cache. Takes a few seconds.">
            <Button
              icon={<FontAwesomeIcon icon={faArrowsRotate} />}
              onClick={() => refreshMutation.mutate(undefined, {
                onSuccess: () => notification.success({
                  message: 'Refresh triggered. New data will appear within a few seconds — reload to see it.',
                }),
                onError: () => notification.error({
                  message: 'Failed to trigger invoicing funnel history refresh.',
                }),
              })}
              loading={refreshMutation.isLoading}
            >
              Refresh
            </Button>
          </Tooltip>
        </div>
      </div>

      <Text type="secondary" className="history-generated-at">
        Cache-backed; refreshed nightly. The newest snapshot reflects the most recent rebuild
        (as-of {dayjs(data.generatedAt).format('MMM D, YYYY h:mm A')}). Invoice volume uses a
        fixed 12-month trailing window ending today across all snapshots, so growth in volume
        reflects cohort growth, not new revenue.
      </Text>

      <div className="history-snapshots">
        {data.snapshots.map((snapshot, i) => (
          <SnapshotRow
            key={snapshot.snapshotDate}
            snapshot={snapshot}
            isLatest={i === 0}
          />
        ))}
      </div>
    </Card>
  )
}

export default InvoicingFunnelHistory
